// @flow
import {Component, ErrorInfo, ReactNode} from 'react';
import {reportError} from '../reportEvent';

type Props = {
    /** Name of the error event */
    name: string;
    /** React component to display when component is in error state. */
    fallbackUI?: ReactNode;
    /**
     * During component error state, this callback function is executed with the same arguments `Error` and `ErrorInfo` as those of `componentDidCatch` in a React error boundary - https://reactjs.org/docs/error-boundaries.html#introducing-error-boundaries.
     */
    onError?: (...errorArgs: [Error, ErrorInfo]) => any;
};

type State = {
    hasError: boolean;
};

export default class ErrorReportBoundary extends Component<Props, State> {
    static getDerivedStateFromError(): State {
        return {hasError: true};
    }
    state = {hasError: false, fallbackUI: null};

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        reportError({
            name: this.props.name,
            componentError: {error}
        });
        this.setState({hasError: true});
        if (typeof this.props.onError === 'function') {
            this.props.onError(error, errorInfo);
        }
    }

    render(): ReactNode | null {
        if (this.state.hasError) {
            return this.props.fallbackUI || null;
        }

        return this.props.children;
    }
}
